/**
 * Confirm / alert dialog in the portal's own chrome (replaces window.confirm
 * and window.alert). Presentation only: the provider owns the promise.
 * Escape and a backdrop click cancel; a destructive confirm focuses Cancel.
 */
import { useEffect, type ReactNode } from 'react';
import { defaultFocus } from './dialogValue';

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  /** null = alert (single button). */
  cancelLabel: string | null;
  danger: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const isAlert = cancelLabel === null;
  const focus = defaultFocus(isAlert ? 'alert' : 'confirm', danger);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="modal"
        role={isAlert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="mt-dialog-title"
        style={{ width: 440 }}
      >
        <div className="modal-head">
          <h2 id="mt-dialog-title">{title}</h2>
        </div>
        {body ? (
          <div className="modal-body" style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2, var(--text))' }}>
            {body}
          </div>
        ) : null}
        <div className="modal-foot">
          {!isAlert ? (
            <button
              type="button"
              className="btn ghost"
              autoFocus={focus === 'cancel'}
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            className={'btn ' + (danger ? 'danger' : 'primary')}
            autoFocus={focus === 'confirm'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
